// The MIT License (MIT)
//
// Copyright (c) 2021 Camptocamp SA
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
// the Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
// FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
// IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
// CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import angular from 'angular';
import pnFormService from '../formservices/formService.js';
import './pronaturaList.css';
import pnForm from '../pronaturaform/pronaturaForm.js';
import FormItem from '../formservices/formItem.js';

/**
 * @type {angular.IModule}
 * @hidden
 */ const myModule = angular.module('pnList', [pnFormService.name, pnForm.name]);

myModule.run(
  /**
   * @ngInject
   * @param {angular.ITemplateCacheService} $templateCache
   */
  ($templateCache) => {
    // @ts-ignore: webpack
    $templateCache.put('pn/pronaturaList', require('./pronaturaList.html'));
  }
);

myModule.value(
  'pnListTemplateUrl',
  /**
   * @param {angular.IAttributes} $attrs Attributes.
   * @return {string} Template URL.
   */
  ($attrs) => {
    const templateUrl = $attrs.pnListTemplateUrl;
    return templateUrl !== undefined ? templateUrl : 'pn/pronaturaList';
  }
);

/**
 * @param {angular.IAttributes} $attrs Attributes.
 * @param {function(angular.IAttributes): string} pnListTemplateUrl Template function.
 * @return {string} Template URL.
 * @ngInject
 * @private
 * @hidden
 */
function pnListTemplateUrl($attrs, pnListTemplateUrl) {
  return pnListTemplateUrl($attrs);
}

/**
 * A list of connections to external tables with button to edit them
 *
 * Example:
 *
 *      <pn-list ng-if="attribute.name == 'gebaeude_id'"
            pn-list-dirty="efCtrl.dirty"
            pn-list-feature="::efCtrl.feature"
            pn-list-type='schaeden'
            pn-list-attribute-name='gebaeude_id'
            pn-list-reference-type="gebaeude"
            pn-small-list-attributes="['fid']">
        </pn-list>
 *
 * @ngdoc component
 * @ngname pnList
 */
const listComponent = {
  controller: 'pnListController as ctrl',
  bindings: {
    dirty: '=pnListDirty',
    feature: '=pnListFeature',
    listType: '@pnListType',
    attributeName: '@pnListAttributeName',
    smallListAttributes: '=pnSmallListAttributes',
    referenceType: '@pnListReferenceType',
  },
  templateUrl: pnListTemplateUrl,
};

myModule.component('pnList', listComponent);

/**
 * @param {angular.IScope} $scope Angular scope.
 * @param {angular.ITimeoutService} $timeout Angular timeout service.
 * @param {JQuery} $element Element.
 * @param {import("../formservices/formService.js").FormService} pnFormService
 * @constructor
 * @private
 * @hidden
 * @ngInject
 * @ngdoc controller
 * @ngname pnListController
 */
export function ListController($scope, $timeout, $element, pnFormService) {
  /**
   * @type {angular.IScope}
   */
  this.scope_ = $scope;

  /**
   * @type {angular.ITimeoutService}
   * @private
   */
  this.$timeout_ = $timeout;

  /**
   * @type {JQuery}
   * @private
   */
  this.element_ = $element;

  /**
   * Flag that controls the visibility of the modal to manage the list elements
   * @type {boolean}
   */
  this.showListEditing = false;

  /**
   * Flag that controls the visibility of the modal to edit an item
   * @type {boolean}
   */
  this.showItemEditing = false;

  /**
   * Number of the item to add to the list
   * @type {number}
   */
  this.newItem = null;

  /**
   * If the form has changed
   * @type {boolean}
   */
  this.dirty = false;

  /**
   * The feature to which the list belongs
   * @type {import("ol/geom/Geometry.js").default}
   */
  this.feature = null;

  /**
   * The item to edit
   * @type {import(""../formservices/formItem.js").FormItem}
   */
  this.item = null;

  /**
   * watch the change of the item
   */
  this.scope_.$watch(
    () => this.item,
    (newVal, oldVal) => this.handleItemChange_(newVal, oldVal)
  );

  /**
   * @type {import("../fromservices/formService.js").FormService}
   * @private
   */
  this.formService_ = pnFormService;

  /**
   * Type of the feature ('parzellen')
   * @type {string}
   */
  this.featureType = null;

  /**
   * Type of item (vertraege)
   * @type {string}
   */
  this.listType = null;

  /**
   * Name of item attribute for adding (vertragsnummer)
   * @type {string}
   */
  this.attributeName = null;

  /**
   * Item attributes to list in the edit panel
   * @type {string[]}
   */
  this.smallListAttributes = null;

  /**
   * Type of feature (parzelle) singular
   * @type {string}
   */
  this.referenceType = null;

  /**
   * If there is a new item to add to the list
   * @type {string}
   */
  this.newItemReady = false;

  /**
   * @type {boolean}
   */
  this.showServerError = false;

  /**
   * @type {?string}
   */
  this.serverErrorMessage = null;

  /**
   * @type {?string}
   */
  this.serverErrorType = null;
}

/**
 * Init the controller
 */
ListController.prototype.$onInit = function () {
  /**
   * watch the change of the item id (new item)
   */
  this.scope_.$watch(
    () => this.newItemReady,
    (newVal, oldVal) => this.handleNewItem_(newVal, oldVal)
  );

  this.itemList = [];
  if (this.feature.get(this.listType)) {
    this.feature.get(this.listType).forEach((item) => {
      this.itemList.push(new FormItem(item, this.listType));
    });
  }
  this.featureNumber = this.feature.get(this.attributeName);
};

/**
 * Toggle display of the list
 */
ListController.prototype.toggleListEditing = function () {
  this.showListEditing = !this.showListEditing;
};

/**
 * Handle change of item
 * @param {import(""../formservices/formItem.js").FormItem} newVal
 * @param {import(""../formservices/formItem.js").FormItem} oldVal
 */
ListController.prototype.handleItemChange_ = function (newVal, oldVal) {
  if (newVal !== oldVal && this.itemList) {
    if (newVal === null) {
      if (oldVal.getId() === null) {
        // item was deleted
        this.removeFromList(oldVal.getId());
      }
    }
  }
};

/**
 * Handle change of newItemReady
 * @param {boolean} newVal
 * @param {boolean} oldVal
 */
ListController.prototype.handleNewItem_ = function (newVal, oldVal) {
  if (newVal) {
    this.addToList(this.item);
    this.newItemReady = false;
  }
};

/**
 * Add a created item to the list
 * @param {import(""../formservices/formItem.js").FormItem} newVal
 */
ListController.prototype.addToList = function (newVal) {
  this.itemList.push(newVal);
};

/**
 * Remove an item from the list
 * @param {number} itemId
 */
ListController.prototype.removeFromList = function (itemId) {
  const index = this.itemList.findIndex((item) => item.getId() === itemId);
  if (index > -1) {
    this.itemList.splice(index, 1);
  }
};

/**
 * Remove a deleted item from the list
 * @param {import(""../formservices/formItem.js").FormItem} itemToRemove
 */
ListController.prototype.delete = function (itemToRemove) {
  this.removeFromList(itemToRemove.getId());
};

/**
 * Edit a new item
 */
ListController.prototype.editNew = function () {
  this.item = new FormItem({}, this.listType);
  this.showItemEditing = true;
};

/**
 * Edit an existing item
 * @param {import(""../formservices/formItem.js").FormItem} item item to edit
 */
ListController.prototype.edit = function (item) {
  this.item = item;
  this.showItemEditing = true;
};

/**
 * Add an item to the list
 */
ListController.prototype.add = function () {
  this.formService_
    .getItemById(this.newItem, this.listType)
    .then((success) => {
      const newFormItem = new FormItem(success.data, this.listType);
      const inList = this.itemList.some((item) => item.getId() == newFormItem.getId());
      if (!inList) {
        this.itemList.push(newFormItem);
      }
    })
    .catch((error) => {
      this.showServerError = true;
      this.serverErrorType = `error type : ${error.data.error_type}`;
      this.serverErrorMessage = `error message : ${error.data.message}`;
    })
    .finally(() => {
      this.pending = false;
    });
};

/**
 * Close the list
 */
ListController.prototype.resetItemList = function () {
  this.$timeout_(() => {
    const simpleItems = this.itemList.map((item) => item.getValues());
    this.feature.set(this.listType, simpleItems);
    this.showListEditing = false;
    this.dirty = true;
  });
};

myModule.controller('pnListController', ListController);

export default myModule;
